import React, { forwardRef, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { LanguageOptionsRegistry, EditorProvider, MonacoEnvironment, } from '@alipay/e2-editor-core';
import _uniq from 'lodash-es/uniq';
import ThemeModule from '@/theme';
import useDeepCompareEffect from '@/hooks/useDeepCompareEffect';
import { needLoadSqlService } from '@/utils';
import { ParentNodeResizeDetector } from '@/utils/resize-detector';
import './scss/editor.scss';
const clsPrefix = `cn-diff-editor`;
const defaultOptions = {
    theme: 'white',
    minimap: {
        enabled: false,
    },
    readOnly: true,
};
const CnCodeDiffEditorCore = forwardRef((props) => {
    const { className, style, language, onCreated, sqlOptions, supportLanguages, editorOptions, original, modified, } = props;
    const { monaco } = window;
    const mergedSupportLanguages = _uniq([...(supportLanguages || []), language]);
    const sqlRegistry = useRef();
    const editorContainer = useRef(null);
    const editorInstance = useRef();
    const registerLanguage = () => {
        MonacoEnvironment.loadModule(async (container) => {
            const { loadModules } = await import('@/loader');
            await loadModules(container, mergedSupportLanguages);
            // 加载自定义主题
            container.load(ThemeModule);
        });
        MonacoEnvironment.init().then(async () => {
            if (!editorContainer.current) {
                return;
            }
            if (sqlOptions) {
                const OptionsRegistry = MonacoEnvironment.container.get(LanguageOptionsRegistry);
                OptionsRegistry.registerOptions(`${language}-sql-service`, sqlOptions);
            }
            if (needLoadSqlService(mergedSupportLanguages)) {
                const sqlService = await import('@alipay/e2-language-sql-service');
                sqlRegistry.current = MonacoEnvironment.container.get(sqlService.SQLModeRegistry);
                sqlRegistry.current.init();
            }
            const editorProvider = MonacoEnvironment.container.get(EditorProvider);
            editorInstance.current = editorProvider.createDiff(editorContainer.current, {
                ...defaultOptions,
                ...editorOptions,
                language,
                original,
                modified,
            });
            if (onCreated) {
                onCreated(editorInstance.current.codeEditor);
            }
        });
    };
    // 监听事件父元素resize
    const onResize = useCallback(() => {
        const codeEditor = editorInstance.current?.codeEditor;
        if (codeEditor) {
            codeEditor.layout();
        }
    }, []);
    useEffect(() => {
        registerLanguage();
        const detector = new ParentNodeResizeDetector(editorContainer.current, onResize);
        return () => {
            editorInstance.current?.codeEditor?.dispose();
            detector.uninstallElementResizeDetector();
        };
    }, []);
    useDeepCompareEffect(() => {
        if (!editorInstance.current) {
            return;
        }
        editorInstance.current.codeEditor.setModel({
            modified: monaco.editor.createModel(modified || '', language),
            original: monaco.editor.createModel(original || '', language),
        });
    }, [modified, original]);
    useDeepCompareEffect(() => {
        if (!editorInstance.current) {
            return;
        }
        editorInstance.current.codeEditor?.updateOptions({
            ...defaultOptions,
            ...editorOptions,
        });
        editorInstance.current.codeEditor?.getOriginalEditor()?.updateOptions({
            ...defaultOptions,
            ...editorOptions,
        });
        editorInstance.current.codeEditor?.getModifiedEditor()?.updateOptions({
            ...defaultOptions,
            ...editorOptions,
        });
    }, [editorOptions]);
    return <div className={classNames(clsPrefix, className)} style={style} ref={editorContainer}/>;
});
CnCodeDiffEditorCore.displayName = 'CnCodeDiffEditorCore';
export { CnCodeDiffEditorCore };
